import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { DeleteOutlineOutlined as DeleteOutlineOutlinedIcon } from '@material-ui/icons';
import { FincenReportContext } from 'hooks/FincenReportContext';
import { FC, useContext } from 'react';
import RadioGroup from 'ui-kit/components/radios/RadioGroup';
import { AgentNetTextInput } from 'ui-kit/inputs/TextField';
import { AddressFields } from 'ui-kit/form/interfaces/AddressModal';
import '../../common/styles/fincen-radio-group.scss';
import { PersonType, TrusteeInfo, ValidationState } from '../../types';
import { AddressType, TRUSTEE_PERSON_OPTIONS, TrusteePersonType } from '../constants';
import AddressBlock from './common/AddressBlock';
import IdentificationForm from './common/IdentificationForm';
import PersonalInfoForm from './common/PersonalInfoForm';
import TransferorAddressBlock from './common/TransferorAddressBlock';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      width: '100%',
    },
    radioGroupContainer: {
      marginBottom: theme.spacing(1),
    },
    sectionHeader: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    section: {
      marginTop: theme.spacing(2),
    },
  }),
);

interface TrusteeFormProps {
  trustee: TrusteeInfo;
  onUpdate: (trustee: TrusteeInfo) => void;
  onDelete: () => void;
  validation: ValidationState;
  trusteeIndex: number;
  transferorIndex: number;
  clearFieldError: (fieldPath: string) => void;
}

const TrusteeForm: FC<TrusteeFormProps> = ({
  trustee,
  onUpdate,
  onDelete,
  validation,
  trusteeIndex,
  transferorIndex,
  clearFieldError,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext(FincenReportContext);
  const transferorValidationErrors = fincenReportCtx?.transferorValidationErrors;
  const generateButtonClicked = fincenReportCtx?.generateButtonClicked;

  const getFieldError = (fieldName: string) => {
    // Only show inline validation errors after Generate Report button is clicked
    if (!generateButtonClicked) return undefined;

    // Always show validation errors for date fields (when generateButtonClicked is true)
    if (fieldName === 'dateOfBirth') {
      return validation.errors.find((error) => {
        return (
          error.field === `[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}` ||
          error.field === `trustees[${trusteeIndex}].${fieldName}` ||
          error.field === `transferors[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`
        );
      });
    }

    // For identification fields, match the full path
    if (fieldName.startsWith('identification.')) {
      const fieldToMatch = `[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`;
      return validation.errors.find((error) => error.field === fieldToMatch);
    }

    const fieldToMatch = `[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`;
    return validation.errors.find((error) => {
      return error.field === fieldToMatch;
    });
  };

  const handleDateOfBirthValidationError = (fieldName: string, message: string) => {
    const fieldPath = `[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`;
    const error = { field: fieldPath, message };
    const existingErrors = transferorValidationErrors || [];
    const filteredErrors = existingErrors.filter((e) => e.field !== fieldPath);
    fincenReportCtx?.setTransferorValidationErrors?.([...filteredErrors, error]);
  };

  const handleDateOfBirthValidationClear = (fieldName: string) => {
    const fieldPath = `[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`;
    clearFieldError(fieldPath);
  };

  // Handle address changes - AddressBlock handles country change logic internally
  const handleAddressChange = (newAddress: AddressFields) => {
    onUpdate({ ...trustee, address: newAddress });
  };

  return (
    <div className={classes.formContainer}>
      <Grid container direction="column" spacing={2}>
        <Grid item xs={12} sm={6} className={classes.radioGroupContainer}>
          <RadioGroup
            label="Person Type"
            name="personType"
            selected={{ value: trustee.personType }}
            onChange={(e) => onUpdate({ ...trustee, personType: e.target.value as PersonType })}
            options={TRUSTEE_PERSON_OPTIONS}
            row={true}
            compact={true}
            border={true}
            className="fincen-radio-group"
            required={true}
            showValidation={!!getFieldError('personType')}
            errs={validation.errors}
            data-testid={`fincen-trustee-${transferorIndex}-${trusteeIndex}-person-type-radio`}
          />
        </Grid>

        {trustee.personType === TrusteePersonType.Individual ? (
          <Grid item xs={12}>
            <PersonalInfoForm
              data={{
                firstName: trustee.firstName || '',
                middleName: trustee.middleName,
                lastName: trustee.lastName || '',
                suffix: trustee.suffix,
                dateOfBirth: trustee.dateOfBirth || '',
              }}
              prefix="trustee"
              index={transferorIndex}
              secondaryIndex={trusteeIndex}
              getFieldError={getFieldError}
              onUpdate={(updates) => onUpdate({ ...trustee, ...updates })}
              onValidationError={handleDateOfBirthValidationError}
              onValidationClear={handleDateOfBirthValidationClear}
              clearFieldError={(fieldName) =>
                clearFieldError(`[${transferorIndex}].trustees[${trusteeIndex}].${fieldName}`)
              }
              showValidation={true}
            />
          </Grid>
        ) : (
          <>
            <Grid item xs={12} sm={6}>
              <AgentNetTextInput
                label="Entity Legal Name"
                value={trustee.entityLegalName || ''}
                onChange={(e) => {
                  onUpdate({ ...trustee, entityLegalName: e.target.value });
                  if (e.target.value) {
                    clearFieldError(`[${transferorIndex}].trustees[${trusteeIndex}].entityLegalName`);
                  }
                }}
                required
                error={!!getFieldError('entityLegalName')}
                helperText={getFieldError('entityLegalName')?.message}
                data-testid={`fincen-trustee-${transferorIndex}-${trusteeIndex}-entity-legal-name-input`}
                fullWidth
              />
            </Grid>
            {((trustee.alternateNames?.length ? trustee.alternateNames : ['']) || ['']).map((name, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                {idx === 0 ? (
                  <AgentNetTextInput
                    label="Alternate Name (i.e., trade name, DBA)"
                    value={name}
                    error={!!getFieldError('alternateNames')}
                    helperText={getFieldError('alternateNames')?.message}
                    onChange={(e) => {
                      const updated = [...(trustee.alternateNames || [''])];
                      updated[idx] = e.target.value;
                      onUpdate({
                        ...trustee,
                        alternateNames: updated,
                      });
                    }}
                    data-testid={`fincen-trustee-${transferorIndex}-${trusteeIndex}-alternate-name-${idx}-input`}
                    fullWidth
                  />
                ) : (
                  <Box display="flex" alignItems="center">
                    <Box flexGrow={1} mr={1}>
                      <AgentNetTextInput
                        label="Alternate Name (i.e., trade name, DBA)"
                        value={name}
                        error={!!getFieldError('alternateNames')}
                        helperText={getFieldError('alternateNames')?.message}
                        onChange={(e) => {
                          const updated = [...(trustee.alternateNames || [])];
                          updated[idx] = e.target.value;
                          onUpdate({
                            ...trustee,
                            alternateNames: updated,
                          });
                        }}
                        data-testid={`fincen-trustee-${transferorIndex}-${trusteeIndex}-alternate-name-${idx}-input`}
                        fullWidth
                      />
                    </Box>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        const updatedAlternateNames = (trustee.alternateNames || []).filter((_, i) => i !== idx);
                        onUpdate({
                          ...trustee,
                          alternateNames: updatedAlternateNames,
                        });
                        clearFieldError(`[${transferorIndex}].trustees[${trusteeIndex}].alternateNames`);
                      }}
                      aria-label="delete alternate name"
                      data-testid={`fincen-trustee-${transferorIndex}-${trusteeIndex}-alternate-name-${idx}-delete-button`}
                      size="small"
                    >
                      <DeleteOutlineOutlinedIcon color="secondary" />
                    </IconButton>
                  </Box>
                )}
              </Grid>
            ))}
          </>
        )}
      </Grid>

      {trustee.personType === TrusteePersonType.Individual && (
        <Box ml={1} mr={1}>
          <Typography variant="h3" className={classes.sectionHeader}>
            Residential Address
          </Typography>
        </Box>
      )}
      {trustee.personType === TrusteePersonType.Individual ? (
        <Box ml={1} mr={1}>
          <AddressBlock
            address={trustee.address}
            onChange={handleAddressChange}
            validation={validation}
            isUSAddress={true}
            fieldPrefix={`[${transferorIndex}].trustees[${trusteeIndex}].address`}
            clearFieldError={clearFieldError}
            isRequired={true}
            transferorIndex={transferorIndex}
            trusteeIndex={trusteeIndex}
          />
        </Box>
      ) : (
        <TransferorAddressBlock
          transferorType={trustee.personType}
          validation={validation}
          address={trustee.address}
          foreignBusinessNoUSLocation={trustee.foreignBusinessNoUSLocation || false}
          foreignBusinessAddress={trustee.foreignBusinessAddress}
          foreignPrincipalAddress={trustee.foreignPrincipalAddress}
          primaryUSAddress={trustee.primaryUSAddress}
          addressType={trustee.addressType as AddressType}
          foreignEntity={trustee.foreignEntity || false}
          onAddressChange={(address) => onUpdate({ ...trustee, address })}
          transferorIndex={transferorIndex}
          trusteeIndex={trusteeIndex}
          addressTypeFieldName={`[${transferorIndex}].trustees[${trusteeIndex}].addressType`}
          clearFieldError={clearFieldError}
          onUpdate={(updates) => {
            const addressFields = ['address', 'foreignBusinessAddress', 'foreignPrincipalAddress', 'primaryUSAddress'];
            Object.keys(updates).forEach((fieldKey) => {
              if (!addressFields.includes(fieldKey)) {
                clearFieldError(`[${transferorIndex}].trustees[${trusteeIndex}].${fieldKey}`);
              }
            });

            onUpdate({
              ...trustee,
              ...updates,
            });
          }}
        />
      )}

      <Grid className={classes.section}>
        <IdentificationForm
          identification={trustee.identification || {}}
          onUpdate={(identification) => onUpdate({ ...trustee, identification })}
          getFieldError={getFieldError}
          clearFieldError={clearFieldError}
          validationErrors={transferorValidationErrors}
          showValidation={!!generateButtonClicked}
          prefix="Trustee"
          transferorType={trustee.personType}
          fieldPrefix={`[${transferorIndex}].trustees[${trusteeIndex}].identification`}
          transferorIndex={transferorIndex}
          trusteeIndex={trusteeIndex}
        />
      </Grid>
    </div>
  );
};

export default TrusteeForm;
